import { useState } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { VideoMetadataThumbnail } from "../../../document-management/components/shared/VideoMetadataThumbnail";
import type { StepVideoBinding } from "../../utils/ragChatUtils";
import { videoDisplayName } from "../../utils/ragChatUtils";

interface StepInlineVideosProps {
  videos: StepVideoBinding[];
  /** Compact layout for step cards; full width in dialogs. */
  compact?: boolean;
}

export function StepInlineVideos({ videos, compact = false }: StepInlineVideosProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<{ title: string; src: string } | null>(null);

  if (!videos.length) return null;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: compact ? 0.75 : 1.25,
          mt: compact ? 1 : 1.5,
        }}
      >
        {videos.map(({ videoNum, item }) => {
          const label =
            item.name ||
            videoDisplayName(item.path, t("opva.chatAssistanceVideoLabel", { num: videoNum }));
          const displayLabel = t("opva.chatAssistanceVideoLabel", { num: videoNum });

          return (
            <Box
              key={`step-video-${videoNum}-${item.path}`}
              component="button"
              type="button"
              onClick={() => setOpen({ title: label, src: item.path })}
              sx={{
                width: "100%",
                p: 0,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                bgcolor: "background.paper",
                overflow: "hidden",
                cursor: "pointer",
                textAlign: "left",
                transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                "&:hover": {
                  borderColor: "primary.light",
                  boxShadow: "0 2px 8px rgba(28,46,54,0.08)",
                },
              }}
            >
              <Box
                sx={{
                  aspectRatio: "16 / 9",
                  bgcolor: "#eef3f8",
                  position: "relative",
                  overflow: "hidden",
                  "& video": {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    pointerEvents: "none",
                  },
                }}
              >
                <VideoMetadataThumbnail src={item.path} fallback={null} />
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Box
                    sx={{
                      width: compact ? 36 : 40,
                      height: compact ? 36 : 40,
                      borderRadius: "50%",
                      bgcolor: "secondary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
                    }}
                  >
                    <PlayArrowIcon
                      sx={{ color: "common.white", fontSize: compact ? 20 : 22, ml: "2px" }}
                    />
                  </Box>
                </Box>
              </Box>
              <Box sx={{ px: 1.25, py: compact ? 0.75 : 1 }}>
                <Typography
                  sx={{
                    color: "text.primary",
                    fontSize: compact ? 12 : 13,
                    fontWeight: 600,
                    lineHeight: 1.35,
                  }}
                >
                  {displayLabel}
                </Typography>
                {label !== displayLabel && (
                  <Typography
                    sx={{
                      mt: 0.25,
                      color: "text.secondary",
                      fontSize: compact ? 11 : 12,
                      lineHeight: 1.35,
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {label}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle>{open?.title}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {open?.src ? (
            <Box
              component="video"
              controls
              src={open.src}
              sx={{ width: "100%", maxHeight: 420, display: "block", bgcolor: "common.black" }}
            />
          ) : (
            <Typography variant="bodySmall" color="text.secondary" sx={{ p: 2 }}>
              {t("opva.chatAssistanceVideoUnavailable")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(null)}>{t("opva.chatAssistanceClose")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
